import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ENTRY_URL, MENU_URL } from '@/App';
import menuIcon from '@/assets/images/menu.png';
import { useLink } from '@/components/LinkToMenu/useLink';
import BrandSkeleton from '@/components/Skeletons/BrandSkeleton';
import TrackedRedirectButton from '@/components/TrackedRedirectButton/TrackedRedirectButton';
import { BUTTON_SOURCES } from '@/constants/buttonSources';
import { useBrand } from '@/hooks/useBrand';
import { useCustomerMenuEnabled } from '@/hooks/useCustomerMenuEnabled';
import { useLanguage } from '@/hooks/useLanguage';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { isValidLogoUrl } from '@/utils/isValidLogoUrl';
import styles from './BrandPresentation.module.scss';

interface Props {
  orderId: string;
}

const BrandPresentation = ({ orderId }: Props) => {
  const { t } = useTranslation();
  const { data, isError, isPending } = useBrand();
  const { language } = useLanguage();
  const { queryParams } = useValidateQueryParams();
  const { tableNumber, counterNumber, brandId, outletId } = queryParams();
  const tableLabel = tableNumber && tableNumber.replace('T', '');
  const { link, isLoading } = useLink();
  const { enabled: isCustomerMenuEnabled, isLoading: isLoadingIsCustomerMenuEnabled } =
    useCustomerMenuEnabled(brandId ?? undefined, outletId ?? undefined);

  const menuButtonId = isCustomerMenuEnabled ? 'view-menu-dine-in' : 'view-menu';
  const menuButtonName = t('menu.entry.viewMenu');

  const menuLinkBase = `${language}${MENU_URL}?outletId=${outletId}&brandId=${brandId}&origin=${ENTRY_URL}`;

  const menuLink = tableNumber
    ? `${menuLinkBase}&tableNumber=${tableNumber}`
    : `${menuLinkBase}&counterNumber=${counterNumber}`;

  const menuButtonTemplate = (
    <div className={styles.menuWrapper} data-testid="view-menu-button">
      <img src={menuIcon} alt="Menu" />
      {menuButtonName}
    </div>
  );

  if (isPending) return <BrandSkeleton />;
  if (isError) return null;

  return (
    <div className={styles.root}>
      <div className={styles.imageWrapper}>
        {data.imageUris && <img src={data.imageUris['720_720']} alt={data.name} />}

        <div className={clsx(styles.header)}>
          <TrackedRedirectButton
            buttonId={menuButtonId}
            buttonName={menuButtonName}
            buttonSource={BUTTON_SOURCES.MAIN_SCREEN}
            data-testid={menuButtonId}
            url={isCustomerMenuEnabled ? menuLink : link}
            data-disabled={!isCustomerMenuEnabled && isLoading}
            urlParams={{
              brandId: brandId || undefined,
              orderId,
              tableNumber,
              counterNumber,
            }}
            isLoading={isLoadingIsCustomerMenuEnabled}
          >
            {menuButtonTemplate}
          </TrackedRedirectButton>
          <div className={styles.brandAndTableWrapper}>
            <div className={styles.brandLogoWrapper}>
              {isValidLogoUrl(data.logoUris['200_200']) && (
                <img src={data.logoUris['200_200']} alt={`${data.name} Logo`} />
              )}
            </div>

            <div className={styles.tableInfo} data-testid="table-info">
              {tableNumber && (
                <>
                  <div className={styles.tableLabel}>{t('menu.brandPresentation.table')}</div>
                  <div className={styles.tableNumber}>{tableLabel}</div>
                </>
              )}
              {counterNumber && <div className={styles.tableNumber}>{counterNumber}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPresentation;
